import flatpickr from 'flatpickr';
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import type { Options as FlatpickrOptions } from 'flatpickr/dist/types/options';

import "./dateTimePicker.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';
import { icon } from "@fortawesome/fontawesome-svg-core";
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";

export class DateTimePicker {
    private static instanceCounter = 0;
    private readonly id: string;
    private fpInstance: FlatpickrInstance;
    private readonly input: HTMLInputElement;
    private static registry = new ComponentRegistry<DateTimePicker>('date-picker');

    private defaultOptions: FlatpickrOptions = {
        disableMobile: true,
        dateFormat: "m/d/Y",
        minDate: "today" // addDays(new Date(), 1)
    };

    // Private constructor - assumes validated input
    private constructor(
        input: HTMLInputElement,
        options: FlatpickrOptions
    ) {
        // Register the Date Picker
        this.input = input;
        this.id = DateTimePicker.registry.register(input, this);
        
        // Apply Flatpickr to single input
        this.fpInstance = flatpickr(input, {
            ...this.defaultOptions,
            ...options
        });
        
        this.createWrapper(input);
    }

    private createWrapper(elem: HTMLInputElement): void {
        // If there's no parent node or we already did this, return
        if (!elem.parentNode || elem.parentElement?.classList.contains('picker-wrapper')) return;

        // Build the wrapper
        const wrapper = document.createElement("div");
        wrapper.classList.add("picker-wrapper");
        elem.parentNode.insertBefore(wrapper, elem);
        wrapper.appendChild(elem);
        
        const svg = getCalendarSvg();
        if (svg) {
            svg.setAttribute('aria-hidden', 'true');
            wrapper.appendChild(svg);
        }
    }

    // Public Methods

    public setDate(date: Date): void {
        this.fpInstance.setDate(date);
    }
    
    public getDate(): Date | null {
        return this.fpInstance.selectedDates[0] || null;
    }

    public destroy(): void {
        this.fpInstance.destroy();
        DateTimePicker.registry.unregister(this.id);
    }

    // Factory Method

    public static create(
        target: string | HTMLInputElement | NodeListOf<HTMLElement> | HTMLElement[], 
        options: FlatpickrOptions = {}
    ): DateTimePicker[] | null {

        const inputs = ComponentRegistry.processTargets<DateTimePicker, HTMLElement>(
            DateTimePicker.registry,
            target,
            [HTMLInputElement]
        ) as HTMLInputElement[];

        if (!inputs.length) return null;

        const visible = inputs.filter(input => input.type !== 'hidden' && !input.hidden);
        if (!visible.length) return null;

        return visible.map(input => new DateTimePicker(input, options));
    }

    public static getPicker(id: string): DateTimePicker | null {
        return DateTimePicker.registry.get(id) ?? null;
    }
}

// Helper Functions

function getCalendarSvg(): SVGElement | null {
    const svgHtml = icon(faCalendarDays).html[0] ?? '';
    if (!svgHtml) return null;
    const frag = document.createRange().createContextualFragment(svgHtml);
    return frag.firstElementChild as SVGElement | null;
}

function addDays(d: Date, days: number): Date {
    const out = new Date(d);
    out.setDate(out.getDate() + days);
    return out;
}